import React, { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Form, Modal, Button } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import InvoiceItemBootstrap from './InvoiceItemBootstrap';

const InvoiceItemListBootstrap = ({
  items = [],
  onItemsChange,
  availableArticles = [],
  roundTotal = false,
  onRoundingChange,
  defaultVatRate = '25%',
  currency = 'SEK'
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItemData, setNewItemData] = useState({
    productcode: '',
    description: '',
    unitprice: 0,
    quantity: 1,
    unit: 'Piece',
    tax_rate: `${defaultVatRate}%`,
    type: 'Goods',
    article_id: null
  });

  // Reset new item data
  const resetNewItemData = useCallback(() => {
    setNewItemData({
      productcode: '',
      description: '',
      unitprice: 0,
      quantity: 1,
      unit: 'Piece',
      tax_rate: `${defaultVatRate}%`,
      type: 'Goods',
      article_id: null
    });
  }, [defaultVatRate]);

  // Show add item modal
  const handleShowAddModal = useCallback(() => {
    resetNewItemData();
    setShowAddModal(true);
  }, [resetNewItemData]);

  // Hide add item modal
  const handleHideAddModal = useCallback(() => {
    setShowAddModal(false);
    resetNewItemData();
  }, [resetNewItemData]);

  // Format currency
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }, [currency]);

  // Format quantity for display (removes trailing zeros)
  // 1.000 → "1", 2.500 → "2.5", 1.250 → "1.25"
  const formatQuantity = useCallback((qty) => {
    if (!qty) return '1';
    const num = parseFloat(qty);
    return num.toFixed(3).replace(/\.?0+$/, '');
  }, []);

  // Format article display
  const getArticleLabel = useCallback((article) => {
    return `${article.productcode || ''} - ${article.description || ''} (${formatCurrency(article.unitprice || 0)})`;
  }, [formatCurrency]);

  // Filter articles by search term
  const filterArticles = useCallback((inputValue) => {
    const searchTerm = inputValue.toLowerCase();
    return availableArticles.filter(article =>
      article.productcode?.toLowerCase().includes(searchTerm) ||
      article.description?.toLowerCase().includes(searchTerm)
    );
  }, [availableArticles]);

  // Load article options for the async select
  const loadArticleOptions = useCallback((inputValue) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const filteredArticles = filterArticles(inputValue);
        const options = filteredArticles.slice(0, 50).map(article => ({
          value: article.id,
          label: getArticleLabel(article),
          article: article
        }));
        resolve(options);
      }, 300);
    });
  }, [filterArticles, getArticleLabel]);

  // Handle article selection in modal
  const handleArticleSelect = useCallback((selectedOption) => {
    if (selectedOption && selectedOption.article) {
      const article = selectedOption.article;
      setNewItemData({
        productcode: article.productcode || '',
        description: article.description || '',
        unitprice: parseFloat(article.unitprice || 0),
        quantity: 1,
        unit: article.unit || 'Piece',
        tax_rate: article.tax_rate || `${defaultVatRate}%`,
        type: article.type || 'Product',
        article_id: article.id
      });
    } else {
      // Clear article data but keep manual entries
      setNewItemData(prev => ({
        ...prev,
        article_id: null
      }));
    }
  }, [defaultVatRate]);

  // Handle manual field changes in modal
  const handleNewItemFieldChange = useCallback((field, value) => {
    setNewItemData(prev => ({
      ...prev,
      [field]: value,
      // Clear article_id if manually editing (unless it's quantity change)
      ...(field !== 'quantity' ? { article_id: null } : {})
    }));
  }, []);

  // Add the configured item to the list
  const handleAddConfiguredItem = useCallback(() => {
    // Validate required fields
    if (!newItemData.description?.trim()) {
      alert('Beskrivning krävs');
      return;
    }

    if (newItemData.unitprice <= 0) {
      alert('Priset måste vara större än 0');
      return;
    }

    // Create new item with UUID
    const newItem = {
      id: uuidv4(),
      ...newItemData,
      isNew: true
    };

    // Add to items list
    onItemsChange([...items, newItem]);

    // Close modal and reset
    handleHideAddModal();
  }, [newItemData, items, onItemsChange, handleHideAddModal]);

  // Quick add from article (bypass modal for known articles)
  const handleQuickAddArticle = useCallback((article) => {
    const newItem = {
      id: uuidv4(),
      productcode: article.productcode || '',
      description: article.description || '',
      unitprice: parseFloat(article.unitprice || 0),
      quantity: 1,
      unit: article.unit || 'Piece',
      tax_rate: article.tax_rate || `${defaultVatRate}%`,
      type: article.type || 'Product',
      article_id: article.id,
      isNew: true
    };

    console.log('Quick adding article:', newItem);
    onItemsChange([...items, newItem]);
  }, [items, onItemsChange, defaultVatRate]);

  // Update existing item
  const handleUpdateItem = useCallback((index, updatedItem) => {
    const newItems = [...items];
    newItems[index] = { ...updatedItem, isModified: true };
    onItemsChange(newItems);
  }, [items, onItemsChange]);

  // Remove item
  const handleRemoveItem = useCallback((index) => {
    const newItems = items.filter((_, idx) => idx !== index);
    onItemsChange(newItems);
  }, [items, onItemsChange]);

  // Select predefined article for existing item
  const handleSelectArticle = useCallback((itemId, articleId) => {
    if (!articleId) return;

    const selectedArticle = availableArticles.find(article => article.id === articleId);
    if (!selectedArticle) return;

    const itemIndex = items.findIndex(item => item.id === itemId);
    if (itemIndex !== -1) {
      const updatedItem = {
        ...items[itemIndex],
        article_id: selectedArticle.id,
        productcode: selectedArticle.productcode || '',
        description: selectedArticle.description || '',
        unitprice: parseFloat(selectedArticle.unitprice || 0),
        unit: selectedArticle.unit || 'Piece',
        tax_rate: selectedArticle.tax_rate || `${defaultVatRate}%`,
        type: selectedArticle.type || 'Product',
        isModified: true
      };

      handleUpdateItem(itemIndex, updatedItem);
    }
  }, [availableArticles, items, defaultVatRate, handleUpdateItem]);

  // Calculate totals with memoization
  const calculations = useMemo(() => {
    const taxGroups = {};
    let subtotal = 0;

    items.forEach(item => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unitprice) || 0;
      const taxRate = parseFloat(item.tax_rate?.replace('%', '')) || 0;

      const lineTotal = quantity * unitPrice;

      if (!taxGroups[taxRate]) {
        taxGroups[taxRate] = { subtotal: 0, tax: 0 };
      }

      taxGroups[taxRate].subtotal += lineTotal;
      taxGroups[taxRate].tax += lineTotal * (taxRate / 100);

      subtotal += lineTotal;
    });

    const totalTax = Object.values(taxGroups).reduce((sum, group) => sum + group.tax, 0);
    const grandTotal = subtotal + totalTax;

    let roundingAmount = 0;
    let finalTotal = grandTotal;

    if (roundTotal && grandTotal > 0) {
      finalTotal = Math.round(grandTotal);
      roundingAmount = finalTotal - grandTotal;
    }

    return {
      taxGroups,
      subtotal,
      totalTax,
      grandTotal,
      roundingAmount,
      finalTotal
    };
  }, [items, roundTotal]);

  // Handle rounding toggle
  const handleRoundingToggle = useCallback((checked) => {
    if (onRoundingChange) {
      onRoundingChange(checked);
    }
  }, [onRoundingChange]);

  return (
    <>
      <style>{`
        .empty-items-state {
          text-align: center;
          padding: 3rem 2rem;
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          margin-bottom: 1rem;
        }
        
        .empty-items-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 1.5rem;
          background: #e9ecef;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6c757d;
          font-size: 1.5rem;
        }
        
        .article-quick-add {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin: 1rem 0;
          justify-content: center;
        }
        
        .article-quick-add .btn {
          font-size: 0.875rem;
          padding: 0.375rem 0.75rem;
        }
        
        .quote-summary {
          margin-top: 2rem;
          padding-top: 1rem;
          border-top: 1px solid #e5e5e5;
        }
        
        .summary-card {
          background: #f8f9fa;
          padding: 1.5rem;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }
        
        .summary-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
          padding: 0.25rem 0;
        }
        
        .summary-row.total {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 2px solid #dee2e6;
          font-weight: bold;
          font-size: 1.1em;
        }
      `}</style>

      <div className="mb-4">
        {items.length === 0 ? (
          <div className="empty-items-state">
            <div className="empty-items-icon">
              <i className="bi bi-basket"></i>
            </div>

            <h3 className="empty-items-title">Inga artiklar tillagda</h3>
            <p className="empty-items-description">
              Lägg till produkter eller tjänster som ska faktureras
            </p>

            <button
              type="button"
              className="btn btn-primary mb-3"
              onClick={handleShowAddModal}
            >
              <i className="bi bi-plus me-2"></i>
              Lägg till artikel eller produkt
            </button>

            {/* Quick add popular articles */}
            {availableArticles.length > 0 && (
              <div>
                <p className="text-muted small mb-2">Eller välj från populära artiklar:</p>
                <div className="article-quick-add">
                  {availableArticles.slice(0, 6).map(article => (
                    <button
                      key={article.id}
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => handleQuickAddArticle(article)}
                      title={article.description}
                    >
                      {article.productcode || article.description?.substring(0, 20) + '...'}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {items.map((item, index) => (
              <InvoiceItemBootstrap
                key={item.id}
                item={item}
                index={index}
                isLast={index === items.length - 1}
                onUpdate={(updatedItem) => handleUpdateItem(index, updatedItem)}
                onRemove={() => handleRemoveItem(index)}
                availableItems={availableArticles}
                onSelectArticle={handleSelectArticle}
                defaultVatRate={defaultVatRate}
                currency={currency}
              />
            ))}

            <div className="mt-4 d-flex justify-content-center">
              <button
                className="btn btn-outline-primary"
                onClick={handleShowAddModal}
              >
                <i className="bi bi-plus me-2"></i>
                Lägg till fler artiklar
              </button>
            </div>
          </>
        )}

        {/* Add Item Modal */}
        <Modal show={showAddModal} onHide={handleHideAddModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Lägg till artikel</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {availableArticles.length > 0 && (
              <div className="mb-4">
                <label className="form-label">Välj från artikelbibliotek</label>
                <AsyncSelect
                  cacheOptions
                  defaultOptions={availableArticles.slice(0, 10).map(article => ({
                    value: article.id,
                    label: getArticleLabel(article),
                    article: article
                  }))}
                  value={newItemData.article_id ? {
                    value: newItemData.article_id,
                    label: getArticleLabel(
                      availableArticles.find(a => a.id === newItemData.article_id) || {}
                    )
                  } : null}
                  loadOptions={loadArticleOptions}
                  onChange={handleArticleSelect}
                  isClearable
                  placeholder="Sök efter artikelkod eller beskrivning..."
                  noOptionsMessage={() => "Inga artiklar hittades"}
                  loadingMessage={() => "Söker artiklar..."}
                />
                <div className="form-text">
                  {newItemData.article_id ? (
                    <span className="text-success">
                      <i className="bi bi-check-circle me-1"></i>
                      Artikel vald från bibliotek
                    </span>
                  ) : (
                    <span className="text-muted">
                      Eller skapa en anpassad artikel nedan
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">Artikelkod</label>
                <input
                  type="text"
                  className="form-control"
                  value={newItemData.productcode}
                  onChange={(e) => handleNewItemFieldChange('productcode', e.target.value)}
                  placeholder="T.ex. ART-001"
                />
              </div>
              <div className="col-md-8">
                <label className="form-label">Beskrivning *</label>
                <input
                  type="text"
                  className="form-control"
                  value={newItemData.description}
                  onChange={(e) => handleNewItemFieldChange('description', e.target.value)}
                  placeholder="Beskrivning av artikeln"
                  required
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-3">
                <label className="form-label">Antal</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  step="0.5"
                  value={newItemData.quantity}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty string for better UX while typing
                    if (value === '') {
                      handleNewItemFieldChange('quantity', '');
                      return;
                    }
                    const parsed = parseFloat(value);
                    if (!isNaN(parsed) && parsed > 0) {
                      handleNewItemFieldChange('quantity', parsed);
                    }
                  }}
                  onBlur={(e) => {
                    // On blur, ensure we have a valid number
                    const value = parseFloat(e.target.value);
                    if (isNaN(value) || value < 0.001) {
                      handleNewItemFieldChange('quantity', 1);
                    }
                  }}
                  placeholder="t.ex. 2.5"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Enhet</label>
                <select
                  className="form-select"
                  value={newItemData.unit}
                  onChange={(e) => handleNewItemFieldChange('unit', e.target.value)}
                >
                  <option value="Piece">st (Piece)</option>
                  <option value="Hour">tim (Hour)</option>
                  <option value="Day">dag (Day)</option>
                  <option value="Month">mån (Month)</option>
                  <option value="Package">frp (Package)</option>
                  <option value="Kg">kg (Kg)</option>
                  <option value="Meter">m (Meter)</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Pris *</label>
                <input
                  type="number"
                  className="form-control"
                  min="0"
                  step="0.01"
                  value={newItemData.unitprice}
                  onChange={(e) => handleNewItemFieldChange('unitprice', parseFloat(e.target.value) || 0)}
                  required
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Moms (%)</label>
                <select
                  className="form-select"
                  value={newItemData.tax_rate?.replace('%', '') || defaultVatRate}
                  onChange={(e) => handleNewItemFieldChange('tax_rate', `${e.target.value}%`)}
                >
                  <option value="0">0</option>
                  <option value="6">6</option>
                  <option value="12">12</option>
                  <option value="25">25</option>
                </select>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Artikeltyp</label>
                <select
                  className="form-select"
                  value={newItemData.type}
                  onChange={(e) => handleNewItemFieldChange('type', e.target.value)}
                >
                  <option value="Service">Tjänst</option>
                  <option value="Goods">Varor</option>
                </select>
              </div>
              <div className="col-md-6 d-flex align-items-end">
                <div className="text-end w-100">
                  <div className="fw-bold">
                    Total: {formatCurrency((newItemData.quantity || 1) * (newItemData.unitprice || 0))}
                  </div>
                  <small className="text-muted">
                    {newItemData.quantity || 1} × {formatCurrency(newItemData.unitprice || 0)}
                  </small>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleHideAddModal}>
              Avbryt
            </Button>
            <Button
              variant="primary"
              onClick={handleAddConfiguredItem}
              disabled={!newItemData.description?.trim() || newItemData.unitprice <= 0}
            >
              <i className="bi bi-plus me-2"></i>
              Lägg till artikel
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Summary section */}
        {items.length > 0 && (
          <div className="quote-summary mt-4">
            <div className="row justify-content-end">
              <div className="col-md-6">
                <div className="summary-card">
                  <div className="summary-row">
                    <span className="label">Delsumma (exkl. moms):</span>
                    <span className="value">{formatCurrency(calculations.subtotal)}</span>
                  </div>

                  {Object.entries(calculations.taxGroups).map(([rate, data]) => (
                    <div className="summary-row" key={rate}>
                      <span className="label">Moms ({rate}%):</span>
                      <span className="value">{formatCurrency(data.tax)}</span>
                    </div>
                  ))}

                  {roundTotal && Math.abs(calculations.roundingAmount) > 0.001 && (
                    <div className="summary-row">
                      <span className="label">Avrundning:</span>
                      <span className="value">{formatCurrency(calculations.roundingAmount)}</span>
                    </div>
                  )}

                  <div className="summary-row total">
                    <span className="label">Totalt (inkl. moms):</span>
                    <span className="value">{formatCurrency(calculations.finalTotal)}</span>
                  </div>

                  <div className="summary-row mt-3">
                    <Form.Check
                      type="switch"
                      id="rounding-switch"
                      label="Avrundning"
                      checked={roundTotal}
                      onChange={(e) => handleRoundingToggle(e.target.checked)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default InvoiceItemListBootstrap;
